<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <v-btn
        class="mr-2"
        color="primary"
        @click="add"
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t('button.add') }}
      </v-btn>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <validation-observer ref="observer">
        <v-data-table
          v-reorder-table
          :headers="headers"
          :items="entities"
          disable-pagination
          mobile-breakpoint="0"
          :no-data-text="noDataText"
          hide-default-footer
          @sorted="changeOrder"
        >
          <template #item.id>
            <v-icon
              class="reorder-handle"
              small
            >
              mdi-arrow-up-down
            </v-icon>
          </template>

          <template #item.name="props">
            <!-- Pop Up Editor -->
            <data-table-input-dialog
              v-model="props.item[nameField]"
              :value.sync="props.item[nameField]"
              rules="required|min:2|max:255"
            />
          </template>

          <template #[extraColumnItem]="props">
            <slot
              :name="extraColumnItem"
              :props="props"
            />
          </template>

          <template #item.actions="props">
            <v-btn
              icon
              @click="remove(props.item.id)"
            >
              <v-icon small>
                mdi-delete
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { mapState } from 'vuex';
import Sortable from 'sortablejs';
import httpClient from '@/api/http-client';
import DataTableInputDialog from '@/components/dialogs/DataTableInputDialog.vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import {
  IApi,
  IModel,
  cloneModel,
  isModelEqualToModel,
  uniqueMinId,
} from '@/model/model-utils';
import { performSaveAction } from '@/services/vue-utils';

export default Vue.extend({
  name: 'SettingsList',
  components: { DataTableInputDialog, SettingsForm },
  mixins: [SettingsBreadcrumbs],
  props: {
    extraColumn: { type: Object, required: false, default: null },
    nameHeaderText: { type: String as PropType<string>, required: true },
    noDataText: { type: String, required: true },
    nameField: { type: String as PropType<string>, default: 'text' },
    storeEntities: { type: Array as PropType<IModel[]>, required: true },
    onSave: { type: Function as PropType<((entities: IModel[]) => Promise<void>)>, required: true },
    onAdd: { type: Function as PropType<(()=>IModel)>, required: true },
    onIsChangedChange: { type: Function as PropType<((value: boolean)=>IModel) | null>, default: null },
  },
  data() {
    return {
      entities: [] as IModel[],
      oldEntities: [] as IModel[],
      extraColumnItem: this.extraColumn !== null ? `item.${this.extraColumn.value}` : 'fake-column',
      unwatch: Function as (() => void),
      headers: [
        {
          text: '',
          width: '32',
          value: 'id',
          align: 'start',
          sortable: false,
        },
        {
          text: this.nameHeaderText,
          value: 'name',
          align: 'start',
          sortable: false,
        },
        this.extraColumn !== null ? this.extraColumn : { sortable: false },
        {
          text: '',
          width: '32',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ],
      breadcrumbs: [],
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.entities, this.oldEntities);
    },
  },
  watch: {
    isChanged(value: boolean, oldValue: boolean) {
      if (this.onIsChangedChange) { this.onIsChangedChange(value); }
    },
  },
  created() {
    this.unwatch = this.$tstore.watch(
      (state, getters) => getters.isLoaded,
      (newValue, oldValue) => {
        this.reset();
      },
    );
  },
  beforeDestroy() {
    this.unwatch();
  },
  mounted() {
    this.reset();
  },
  methods: {
    changeOrder(event: Sortable.SortableEvent) {
      if (event.oldIndex === undefined || event.newIndex === undefined) return;
      const movedItem = this.entities.splice(event.oldIndex, 1)[0];
      this.entities.splice(event.newIndex, 0, movedItem);
    },
    add() {
      const e = this.onAdd();
      e.id = uniqueMinId(this.entities);
      this.entities.push(e);
    },
    remove(id: number) {
      this.entities = this.entities.filter((o: IModel) => o.id !== id);
    },
    reset() {
      this.oldEntities = this.storeEntities;
      this.entities = this.storeEntities.map((o) => cloneModel(o));
    },
    async save() {
      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.onSave(this.entities),
      );

      if (ok) {
        this.reset();
      }
    },
  },
});
</script>
